import React, { useState, useEffect } from 'react';
import { usersAPI, clustersAPI, serversAPI, organizationsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ALL_SECTIONS, ACTIONS, sectionLabel, sectionCovers } from '../constants/sectionLabels';
import ActionsMenu, { MENU_ITEM } from '../components/ActionsMenu';

const ROLES = ['superadmin', 'admin', 'member'];

const ROLE_BADGE = {
  superadmin: { bg: 'var(--accent)', label: 'Super Admin' },
  admin:      { bg: 'var(--brand)', label: 'Admin' },
  member:     { bg: 'var(--color-success-500)', label: 'Member' },
};

function RoleBadge({ role }) {
  const b = ROLE_BADGE[role] || ROLE_BADGE.member;
  return (
    <span className="badge" style={{ background: b.bg, color: '#fff' }}>
      {b.label}
    </span>
  );
}

function UserManagement() {
  const { isSuperAdmin, isAdmin, isPrimeAdmin, user } = useAuth();
  const navigate = useNavigate();
  // Org admins can self-service create/promote fellow admins within their own
  // org (no superadmin involvement needed) — but never assign 'superadmin'.
  const assignableRoles = isSuperAdmin ? ROLES : ['member', 'admin'];
  // Org admins can only grant sections their org is entitled to.
  const availableSections = isSuperAdmin
    ? ALL_SECTIONS
    : ALL_SECTIONS.filter(s => (user?.org_sections || []).includes(s));
  // Verbs the current admin may grant on a section (org is the top cap; superadmin unrestricted).
  const verbCap = (sec) => isSuperAdmin
    ? ACTIONS
    : ((user?.org_section_actions || {})[sec] || []);

  const [users, setUsers] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [servers, setServers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [filterOrg, setFilterOrg] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create user modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ username: '', email: '', password: '', full_name: '', phone_number: '', rbac_role: 'member', organization_id: '' });
  const [createError, setCreateError] = useState('');
  const [showCreatePassword, setShowCreatePassword] = useState(false);

  // Edit permissions modal
  const [editUser, setEditUser] = useState(null);
  const [editSectionActions, setEditSectionActions] = useState({}); // {section:[verbs]}
  const [editClusters, setEditClusters] = useState([]);
  const [editServers, setEditServers] = useState([]);
  const [editRole, setEditRole] = useState('');
  const [editNewPassword, setEditNewPassword] = useState('');
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    if (!isAdmin) { navigate('/'); return; }
    load();
  }, [isAdmin, navigate]);

  useEffect(() => { if (isAdmin) load(); }, [filterOrg]);

  async function load() {
    setLoading(true);
    try {
      // Users are the critical dependency and are role-gated (no section needed).
      const uRes = await usersAPI.list(filterOrg || null);
      setUsers(uRes.data);
      setError('');
      // Clusters/servers/orgs only feed the edit-modal whitelists and org names.
      // Tolerate 403 when the org isn't entitled to the clusters/servers sections —
      // user management must keep working regardless.
      const [cRes, sRes, oRes] = await Promise.allSettled([
        clustersAPI.list(),
        serversAPI.list(),
        organizationsAPI.list(),
      ]);
      setClusters(cRes.status === 'fulfilled' ? cRes.value.data : []);
      setServers(sRes.status === 'fulfilled' ? sRes.value.data : []);
      setOrgs(oRes.status === 'fulfilled' ? oRes.value.data : []);
    } catch (e) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    setCreateError('');
    try {
      const payload = { ...createForm };
      delete payload.organization_id; // always assigned by API to admin's org
      await usersAPI.create(payload);
      setShowCreate(false);
      setCreateForm({ username: '', email: '', password: '', full_name: '', phone_number: '', rbac_role: 'member' });
      load();
    } catch (e) {
      setCreateError(e.response?.data?.detail || 'Failed to create user');
    }
  }

  function openEdit(u) {
    setEditUser(u);
    setEditRole(u.rbac_role);
    setEditSectionActions(Object.fromEntries((u.permissions || []).map(p => [p.section, (p.actions && p.actions.length) ? p.actions : ['view','create','edit','delete']])));
    setEditClusters(u.cluster_access_ids || []);
    setEditServers(u.server_access_ids || []);
    setEditNewPassword('');
    setShowEditPassword(false);
    setEditError('');
  }

  async function handleSaveEdit() {
    if (!editUser) return;
    setEditLoading(true);
    setEditError('');
    try {
      const userUpdate = { rbac_role: editRole };
      if (editNewPassword) userUpdate.password = editNewPassword;
      // Run all 4 updates in parallel — faster and avoids sequential failure masking
      const [r1, r2, r3, r4] = await Promise.allSettled([
        usersAPI.update(editUser.id, userUpdate),
        usersAPI.assignPermissions(editUser.id, { section_actions: editSectionActions }),
        usersAPI.assignClusters(editUser.id, editClusters),
        usersAPI.assignServers(editUser.id, editServers),
      ]);
      const failed = [r1, r2, r3, r4].filter(r => r.status === 'rejected');
      if (failed.length > 0) {
        const msgs = failed.map(r => r.reason?.response?.data?.detail || r.reason?.message || 'Unknown error');
        setEditError(msgs.join(' | '));
        return;
      }
      setEditUser(null);
      load();
    } catch (e) {
      setEditError(e.response?.data?.detail || 'Failed to save');
    } finally {
      setEditLoading(false);
    }
  }

  async function handleDelete(u) {
    if (!window.confirm(`Delete user "${u.username}"?`)) return;
    try {
      await usersAPI.delete(u.id);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete user');
    }
  }

  function toggleUserVerb(section, verb) {
    setEditSectionActions(prev => {
      const cur = prev[section] || [];
      let next = cur.includes(verb) ? cur.filter(v => v !== verb) : [...cur, verb];
      if (next.length && !next.includes('view')) next = ['view', ...next];
      const out = { ...prev };
      if (next.length) out[section] = next; else delete out[section];
      return out;
    });
  }

  function toggleCluster(id) {
    setEditClusters(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }

  function toggleServer(id) {
    setEditServers(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }

  if (loading) return <div style={{ padding: 32 }}>Loading users...</div>;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>User Management</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: 14 }}>Manage users, roles, section permissions, cluster and server access</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {isSuperAdmin && (
            <select value={filterOrg} onChange={e => setFilterOrg(e.target.value)}
              className="select" style={{ width: 'auto' }}>
              <option value="">All Organizations</option>
              {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
          <button onClick={() => setShowCreate(true)} className="btn btn-primary">

            + Add User
          </button>
        </div>
      </div>

      {error && <div className="banner danger" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="table-card">
      <table className="ds">
        <thead>
          <tr>
            {['Name', 'Username', 'Email', 'Org', 'Role', 'Sections', 'Clusters', 'Servers', 'Status', 'Actions'].map(h => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {users.map(u => (
            <tr key={u.id}>
              <td>{u.full_name || '—'}</td>
              <td style={{ fontWeight: 600 }}>{u.username}</td>
              <td style={{ fontSize: 13, color: 'var(--muted)' }}>{u.email}</td>
              <td>
                <span className="badge">
                  {orgs.find(o => o.id === u.organization_id)?.name || `org#${u.organization_id}`}
                </span>
              </td>
              <td><RoleBadge role={u.rbac_role} /></td>
              <td style={{ fontSize: 12, color: 'var(--muted)' }}>
                {['admin', 'superadmin'].includes(u.rbac_role)
                  ? <span style={{ color: 'var(--color-success-500)', fontWeight: 600 }}>All</span>
                  : ((u.permissions || []).length === 0
                    ? <span style={{ color: 'var(--muted-2)' }}>None</span>
                    : (u.permissions || []).map(p => p.section || p).join(', '))
                }
              </td>
              <td style={{ fontSize: 12, color: 'var(--muted)' }}>
                {['admin', 'superadmin'].includes(u.rbac_role)
                  ? <span style={{ color: 'var(--color-success-500)', fontWeight: 600 }}>All</span>
                  : ((u.cluster_access_ids || []).length === 0
                    ? <span style={{ color: 'var(--muted-2)' }}>None</span>
                    : (u.cluster_access_ids || []).map(id => {
                        const c = clusters.find(x => x.id === id);
                        return c ? c.name : `#${id}`;
                      }).join(', '))
                }
              </td>
              <td style={{ fontSize: 12, color: 'var(--muted)' }}>
                {['admin', 'superadmin'].includes(u.rbac_role)
                  ? <span style={{ color: 'var(--color-success-500)', fontWeight: 600 }}>All</span>
                  : ((u.server_access_ids || []).length === 0
                    ? <span style={{ color: 'var(--muted-2)' }}>None</span>
                    : (u.server_access_ids || []).map(id => {
                        const s = servers.find(x => x.id === id);
                        return s ? s.name : `#${id}`;
                      }).join(', '))
                }
              </td>
              <td>
                <span style={{ color: u.is_active ? 'var(--color-success-500)' : 'var(--color-danger-500)', fontWeight: 600, fontSize: 13 }}>
                  {u.is_active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td>
                <ActionsMenu>
                  <button onClick={() => openEdit(u)} style={MENU_ITEM}>
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(u)}
                    disabled={u.rbac_role === 'admin' && !isPrimeAdmin}
                    title={u.rbac_role === 'admin' && !isPrimeAdmin
                      ? 'Only a prime admin (appointed by a superadmin) can delete another admin'
                      : undefined}
                    style={{ ...MENU_ITEM, color: 'var(--color-danger-500)' }}
                  >
                    Delete
                  </button>
                </ActionsMenu>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {/* Create User Modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 32, width: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ margin: '0 0 20px', fontSize: 18 }}>Create New User</h2>
            {createError && <div className="banner danger" style={{ marginBottom: 12 }}>{createError}</div>}
            <form onSubmit={handleCreate}>
              {[
                { label: 'Username', key: 'username', type: 'text', required: true },
                { label: 'Email', key: 'email', type: 'email', required: true },
                { label: 'Password', key: 'password', type: 'password', required: true },
                { label: 'Full Name', key: 'full_name', type: 'text', required: false },
                { label: 'Phone (for 2FA, e.g. 9876543210)', key: 'phone_number', type: 'tel', required: false },
              ].map(f => (
                <div key={f.key} style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{f.label}{f.required && ' *'}</label>
                  {f.key === 'password' ? (
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showCreatePassword ? 'text' : 'password'}
                        value={createForm.password}
                        onChange={e => setCreateForm(p => ({ ...p, password: e.target.value }))}
                        required
                        className="input"
                        style={{ width: '100%', boxSizing: 'border-box', paddingRight: 40 }}
                      />
                      <button type="button" onClick={() => setShowCreatePassword(s => !s)}
                        title={showCreatePassword ? 'Hide password' : 'Show password'}
                        style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--muted)' }}>
                        {showCreatePassword ? '🙈' : '👁'}
                      </button>
                    </div>
                  ) : (
                    <input
                      type={f.type}
                      value={createForm[f.key]}
                      onChange={e => setCreateForm(p => ({ ...p, [f.key]: e.target.value }))}
                      required={f.required}
                      className="input"
                      style={{ width: '100%', boxSizing: 'border-box' }}
                    />
                  )}
                </div>
              ))}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Role *</label>
                <select value={createForm.rbac_role} onChange={e => setCreateForm(p => ({ ...p, rbac_role: e.target.value }))}
                  className="select" style={{ width: '100%' }}>
                  {assignableRoles.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowCreate(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Permissions Modal */}
      {editUser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 32, width: 560, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Edit User: {editUser.username}</h2>
            <p style={{ margin: '0 0 20px', color: 'var(--muted)', fontSize: 13 }}>{editUser.email}</p>

            {editError && <div className="banner danger" style={{ marginBottom: 12 }}>{editError}</div>}

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Role</label>
              {editUser.rbac_role === 'admin' && !isPrimeAdmin && (
                <div style={{ fontSize: 12, color: 'var(--color-warning-600, #b45309)', marginBottom: 6 }}>
                  Only a prime admin (appointed by a superadmin) can change another admin's role.
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {assignableRoles.map(r => (
                  <button
                    key={r}
                    disabled={editUser.rbac_role === 'admin' && !isPrimeAdmin}
                    onClick={() => setEditRole(r)}
                    style={{
                      padding: '6px 16px', borderRadius: 20, border: '2px solid',
                      borderColor: editRole === r ? ROLE_BADGE[r].bg : 'var(--border)',
                      background: editRole === r ? ROLE_BADGE[r].bg : 'var(--surface)',
                      color: editRole === r ? '#fff' : 'var(--ink-2)',
                      cursor: (editUser.rbac_role === 'admin' && !isPrimeAdmin) ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13,
                    }}
                  >
                    {ROLE_BADGE[r].label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                New Password
                <span style={{ fontWeight: 400, color: 'var(--muted)', marginLeft: 6, fontSize: 12 }}>— leave blank to keep current password</span>
              </label>
              <div style={{ position: 'relative', maxWidth: 280 }}>
                <input
                  type={showEditPassword ? 'text' : 'password'}
                  value={editNewPassword}
                  onChange={e => setEditNewPassword(e.target.value)}
                  placeholder="•••••••••"
                  className="input"
                  style={{ width: '100%', boxSizing: 'border-box', paddingRight: 40 }}
                />
                <button type="button" onClick={() => setShowEditPassword(s => !s)}
                  title={showEditPassword ? 'Hide password' : 'Show password'}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--muted)' }}>
                  {showEditPassword ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            {editRole === 'member' && (
              <>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Section Permissions — Member Grants</label>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                    Grant per-section actions — each section governs the sidebar pages listed under it below. You can only grant what your organization allows (greyed = not available). View is implied by any grant.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {availableSections.map(s => {
                      const cap = verbCap(s);
                      const cur = editSectionActions[s] || [];
                      return (
                        <div key={s} style={{ padding: '8px 10px', borderRadius: 6, background: cur.length ? '#eff6ff' : '#f9fafb', border: `1px solid ${cur.length ? '#bfdbfe' : '#e5e7eb'}` }}>
                          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                            <span style={{ minWidth: 150, fontWeight: 600, fontSize: 13 }}>{sectionLabel(s)}</span>
                            <div style={{ display: 'flex', gap: 12 }}>
                              {ACTIONS.map(a => {
                                const allowed = cap.includes(a);
                                return (
                                  <label key={a} style={{ display: 'flex', alignItems: 'center', gap: 4, width: 64, fontSize: 12, textTransform: 'capitalize', color: allowed ? '#374151' : '#cbd5e1', cursor: allowed ? 'pointer' : 'not-allowed' }}>
                                    <input type="checkbox" disabled={!allowed} checked={cur.includes(a)} onChange={() => toggleUserVerb(s, a)} style={{ accentColor: '#2563eb', flexShrink: 0 }} />
                                    {a}
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                          {sectionCovers(s) && (
                            <div style={{ fontWeight: 400, fontSize: 10, color: '#9ca3af', marginTop: 4 }}>{sectionCovers(s)}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                    Cluster Access Whitelist
                    <span style={{ fontWeight: 400, color: 'var(--muted)', marginLeft: 6 }}>(none = no access)</span>
                  </label>
                  {clusters.length === 0
                    ? <p style={{ color: 'var(--muted-2)', fontSize: 13 }}>No clusters available</p>
                    : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {clusters.map(c => (
                          <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, padding: '8px 12px', borderRadius: 6, background: editClusters.includes(c.id) ? 'var(--color-success-50)' : 'var(--surface-2)', border: `1px solid ${editClusters.includes(c.id) ? 'var(--color-success-500)' : 'var(--border)'}` }}>
                            <input
                              type="checkbox"
                              checked={editClusters.includes(c.id)}
                              onChange={() => toggleCluster(c.id)}
                              style={{ accentColor: 'var(--color-success-500)' }}
                            />
                            <span style={{ fontWeight: 600 }}>{c.name}</span>
                            <span style={{ color: 'var(--muted-2)', fontSize: 12 }}>({c.environment})</span>
                          </label>
                        ))}
                      </div>
                    )
                  }
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                    Server Access Whitelist
                    <span style={{ fontWeight: 400, color: 'var(--muted)', marginLeft: 6 }}>(none = no access)</span>
                  </label>
                  {servers.length === 0
                    ? <p style={{ color: 'var(--muted-2)', fontSize: 13 }}>No servers available</p>
                    : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {servers.map(s => (
                          <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, padding: '8px 12px', borderRadius: 6, background: editServers.includes(s.id) ? 'var(--accent-soft)' : 'var(--surface-2)', border: `1px solid ${editServers.includes(s.id) ? 'var(--color-accent-100)' : 'var(--border)'}` }}>
                            <input
                              type="checkbox"
                              checked={editServers.includes(s.id)}
                              onChange={() => toggleServer(s.id)}
                              style={{ accentColor: 'var(--accent)' }}
                            />
                            <span style={{ fontWeight: 600 }}>{s.name}</span>
                            {s.ip_address && <span style={{ color: 'var(--muted-2)', fontSize: 12 }}>({s.ip_address})</span>}
                          </label>
                        ))}
                      </div>
                    )
                  }
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditUser(null)} className="btn btn-secondary">Cancel</button>
              <button onClick={handleSaveEdit} disabled={editLoading} className="btn btn-primary">
                {editLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
